import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LgdFormControlFactory, setEnabled } from "@tylertech/lgd-reactive-forms";
import { VendorLookupService } from "@tylertech/accounts-payable-module";
import { ExecutionResult, VendorLookupViewModel } from "@tylertech/tyler-api-types";
import { Command, IconCommand } from "@tylertech/commands-module";
import { BidQuoteModel } from "../../interfaces";
@Component({
    selector: "bid-quote",
    templateUrl: "./bidQuote.tmpl.html",
    styleUrls: ["./bidQuote.component.scss"],
    standalone: false
})
export class BidQuoteComponent implements OnInit, OnDestroy
{
    private readonly $vendorLookupService = inject(VendorLookupService);

    private readonly destructor$ = new Subject<void>();
    private readonly vendorSubject = new BehaviorSubject<Array<VendorLookupViewModel>>([]);
    readonly bidQuoteForm: FormGroup;
    readonly deleteCmd: Command;
    @Input()
    canEdit: boolean;

    @Input()
    model: BidQuoteModel;

    @Output()
    readonly onDelete = new EventEmitter<BidQuoteModel>();

    @Output()
    readonly viewAttachments = new EventEmitter<BidQuoteModel>();

    @Output()
    readonly viewNotes = new EventEmitter<BidQuoteModel>();

    constructor()
    {
        this.bidQuoteForm = new FormGroup({
            vendor: LgdFormControlFactory.typeahead<VendorLookupViewModel>(undefined, true, {
                displayValueFn: x => this.getTemplateText(x),
            }),
            total: LgdFormControlFactory.currency(undefined, true, {
                min: 0,
            }),
            awarded: LgdFormControlFactory.checkbox(undefined, false, {}),
            awardedDate: LgdFormControlFactory.date(undefined, () => this.model?.Awarded, {}),
        });
        this.deleteCmd = new IconCommand({
            icon: "delete",
            label: "Delete",
            action: () => this.delete(),
            getEnabled: () => this.canEdit,
        });
    }

    private get vendorControl(): AbstractControl<VendorLookupViewModel>
    {
        return this.bidQuoteForm.get("vendor");
    }

    private get totalControl(): AbstractControl<number>
    {
        return this.bidQuoteForm.get("total");
    }

    private get awardedControl(): AbstractControl<boolean>
    {
        return this.bidQuoteForm.get("awarded");
    }

    private get awardedDateControl(): AbstractControl<Date>
    {
        return this.bidQuoteForm.get("awardedDate");
    }

    get vendorObservable(): Observable<unknown[]>
    {
        return this.vendorSubject.asObservable();
    }

    get VendorName(): string
    {
        const vendor = !this.model ? null : this.model.Vendor;
        return this.getTemplateText(vendor);
    }

    get Notes(): number
    {
        return this.model.Notes?.filter(x => !x.IsDeleted).length;
    }

    get Attachments(): number
    {
        return this.model.Attachments?.filter(x => !x.IsDeleted).length;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;
        this.vendorControl.setValue(this.model.Vendor);
        subscribeToValueChanges(this.vendorControl, (newValue: VendorLookupViewModel) =>
        {
            this.model.Vendor = newValue;
        });
        this.totalControl.setValue(this.model.Total);
        subscribeToValueChanges(this.totalControl, (newValue: number) =>
        {
            this.model.Total = newValue;
        });
        setEnabled(this.awardedControl, this.canEdit).setValue(this.model.Awarded);
        subscribeToValueChanges(this.awardedControl, (newValue: boolean) =>
        {
            this.model.Awarded = newValue;
            setEnabled(this.awardedDateControl, this.canEdit && this.model.Awarded);
        });
        setEnabled(this.awardedDateControl, this.canEdit && this.model.Awarded).setValue(this.model.AwardDate);
        subscribeToValueChanges(this.awardedDateControl, (newValue: Date) =>
        {
            this.model.AwardDate = newValue;
        });
        function subscribeToValueChanges<T = unknown>(
            control: AbstractControl<T>,
            subscriptionFn: (newValue: T) => void
        ): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$)).subscribe(x =>
            {
                subscriptionFn(x);
            });
        }
    }

    private delete(): void
    {
        this.onDelete.emit(this.model);
    }

    private getTemplateText(vendor: VendorLookupViewModel): string
    {
        return vendor ? `${vendor.Number} - ${vendor.Name}` : "No Vendor";
    }

    lookupVendors($event: string): void
    {
        this.$vendorLookupService
            .Lookup({ SearchText: $event })
            .then((executionResult: ExecutionResult<Array<VendorLookupViewModel>>) =>
            {
                this.vendorSubject.next(executionResult.Result);
            });
    }

    showAttachments(): void
    {
        this.viewAttachments.emit(this.model);
    }

    showNotes(): void
    {
        this.viewNotes.emit(this.model);
    }
}
